import { type Money } from "./application";

export enum PropertyKind {
  MultiFamily = "multi_family",
  SingleFamily = "single_family"
}

export enum MediaAttachmentResolution {
  Full = "full",
  Small = "small"
}
interface MediaAttachment {
  global_id: string;
  url: string;
  content_type: string | null;
  original_filename: string | null;
  checksum: string | null;
  extension: string;
  size: number;
  resolution: MediaAttachmentResolution;
}

export interface MediaItem {
  id: number;
  position: number;
  is_cover: boolean;
  created_at: string;
  updated_at: string | null;
  attachments?: MediaAttachment[];
}

export interface ListingPlan {
  beds: number;
  sqft: number;
  baths: number;
}

export interface ImageVariantUrl {
  full: string;
  small: string;
}

interface Amenity {
  key: string;
  level: "property" | "unit";
  title: string;
}

export interface PartnerDetails {
  apply_token: string;
  amenities: {
    property: Amenity[];
    unit: Amenity[];
  };
  name: string;
  slug: string;
  url: string;
  asset_logo_url: string | null;
  colors?: {
    primary: Record<string, string>;
    background?: Record<string, string>;
  };
  phone_number: string | null;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone_number: string;
  email: string;
}

export interface PropertyDetails {
  name: string;
  slug: string;
  kind: PropertyKind;
  address: {
    one_line_address: string;
    city: string;
    state_or_province: string;
    postal_code: string;
    country: string;
  };
  branding: {
    landlord_name?: string;
    landlord_phone?: string;
    logo?: {
      value: string;
      asset: {
        url: string;
        content_type: string;
        filename: string;
        size: number;
        extension: string;
      };
    };
    palette?: Record<string, string>;
  };
}

export interface MapPin {
  slug: string;
  lat: number;
  lng: number;
  unit_url: string;
  title: string;
  primary_image_url: ImageVariantUrl | null;
  monthly_rent_amount: Money | null;
  home_rent_amount: Money | null;
  site_rent_amount: Money | null;
  total_rent_amount: Money | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  occupied: boolean;
  property_name: string;
  property_address: string;
}

interface BaseListing {
  id: number;
  slug: string;
  partner_slug: string;
  title: string;
  description: string | null;
  primary_image_url: ImageVariantUrl | null;
  gallery_urls: ImageVariantUrl[];
  monthly_rent_amount: Money | null;
  home_rent_amount: Money | null;
  site_rent_amount: Money | null;
  total_rent_amount: Money | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  available_at: string;
  occupied: boolean;
  cats_allowed: boolean;
  dogs_allowed: boolean;
  city: string;
  state_or_province: string;
  postal_code: string;
  unit_url: string;
  lat: number | null;
  lng: number | null;
  media: MediaItem[];
  property: PropertyDetails;
  amenities: {
    id: number;
    title: string;
  }[];
}

interface WithML {
  can_apply: true;
  magic_link: {
    id: string;
    monthly_rent_amount: Money;
    created_at: string;
    updated_at: string;
    link: string;
    active: boolean;
    name: string | null;
    email: string | null;
    unit_id: string;
    property_id: string;
    application_id: string | null;
    kind: "unit";
  };
}

interface WithoutML {
  can_apply: false;
}

export type Listing = BaseListing & (WithML | WithoutML);
